import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  Input,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import {
  ChartDataset,
  ChartTypeRegistry,
  TooltipCallbacks,
  TooltipItem
} from 'chart.js';
import { _DeepPartialObject } from 'chart.js/dist/types/utils';
import { isSameDay, isSameMonth } from 'date-fns';
import { Anlegg } from 'src/app/services/forbruk/forbruk.types';
import {
  labelTooltipCallback,
  titleTooltipCallback
} from '../../forbruk-charts-utils';
import { hasChanged } from 'src/app/utils/change-utils';
import { getDaysInMonth } from 'src/app/utils/date-utils';

@Component({
  selector: 'app-day-chart-usage',
  templateUrl: './day-chart-usage.component.html',
  styleUrls: ['./day-chart-usage.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DayChartUsageComponent implements OnInit, OnChanges {
  @Input() valgtAnlegg: Anlegg;
  @Input() selectedDate: Date;

  dataSets: ChartDataset<'bar'>[];
  labels: Date[];
  title: string;
  tooltipCallbacks: _DeepPartialObject<TooltipCallbacks<'bar'>>;
  showLegend = false;
  useCurrentMonth: boolean;
  tooltipFilter: (item: TooltipItem<keyof ChartTypeRegistry>) => boolean;

  constructor() { }

  ngOnInit(): void {
    if (this.valgtAnlegg && this.selectedDate) {
      this.getChartData();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      this.valgtAnlegg &&
      this.selectedDate &&
      hasChanged([changes?.valgtAnlegg])
    ) {
      this.getChartData();
    }
  }

  getChartData() {
    this.dataSets = [];

    // Plusskunde produksjon
    const proddata = [];
    let plusskundedataFound = false;

    this.title = 'Strømforbruk';
    this.tooltipCallbacks = {
      label: (tt) => labelTooltipCallback(tt, 'kWh'),
      title: (tt) =>
        titleTooltipCallback(tt, 'day', 'kWh', 'forbruk_tooltip_chart1')
    };
    this.tooltipFilter = (item) => {
      return item.raw !== 0;
    };

    // Generate labels for whole month
    const noOfDays = getDaysInMonth(this.selectedDate);

    this.labels = [...Array(noOfDays).keys()].map(n => new Date(
      this.selectedDate?.getFullYear(), this.selectedDate.getMonth(), n + 1
    ));

    const forbruksData = this.labels.map((date) => {
      const value = this.valgtAnlegg.verdiList.find((v) =>
        isSameDay(new Date(v.tidspunkt), date)
      );

      if (value && isSameMonth(date, this.selectedDate)) {
        this.useCurrentMonth = true;

        if (this.valgtAnlegg.plusskunde) {
          proddata.push(value.plussverdi || 0);
          if (value.plussverdi > 0) plusskundedataFound = true;
        }

        return value.verdi || 0;
      } else {
        if (this.valgtAnlegg.plusskunde) proddata.push(0);
        return 0;
      }
    });

    if (this.useCurrentMonth) {
      if (plusskundedataFound) {
        this.dataSets.push({
          data: forbruksData,
          label: 'Forbruk',
          backgroundColor: '#002987'
        });
        this.dataSets.push({
          data: proddata,
          label: 'Overskudd',
          backgroundColor: '#26b41b'
        });
      } else {
        this.dataSets.push({
          data: forbruksData,
          backgroundColor: '#002987',
          label: 'Forbruk',
          order: 1
        });
      }
    }

    this.showLegend = plusskundedataFound;
  }
}
